/**
 * A repeating check that cleans up after itself.
 *
 * The interval it replaces outlived its component, kept running after a failed
 * tick had thrown out of the callback, and had no end: a customer who never
 * finished at their bank was left watching a spinner poll forever.
 */
export class Poller {

    /**
     * @param intervalMillis  gap between ticks
     * @param timeoutMillis   give up after this long
     * @param onTick          async; return true to keep going, false to stop
     * @param onError         called instead of throwing out of the interval
     * @param onTimeout       called once when the deadline passes
     */
    constructor({ intervalMillis, timeoutMillis, onTick, onError = () => {}, onTimeout = () => {} }) {
        this.intervalMillis = intervalMillis;
        this.timeoutMillis = timeoutMillis;
        this.onTick = onTick;
        this.onError = onError;
        this.onTimeout = onTimeout;
        this.timer = null;
        this.startedAt = null;
    }

    get isRunning() {
        return this.timer !== null;
    }

    /** Restarts the clock if already running. */
    start() {
        this.stop();
        this.startedAt = Date.now();
        this.timer = setInterval(() => this.tick(), this.intervalMillis);
    }

    stop() {
        if (this.timer !== null) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    async tick() {
        if (Date.now() - this.startedAt > this.timeoutMillis) {
            this.stop();
            this.onTimeout();
            return;
        }

        try {
            let shouldContinue = await this.onTick();
            if (!shouldContinue) {
                this.stop();
            }
        } catch (e) {
            // one failed check is not a reason to stop watching
            this.onError(e);
        }
    }
}
